import { c as _c } from "react/compiler-runtime";
import { Portal } from '@ark-ui/react/portal';
import { Tooltip as ArkTooltip } from '@ark-ui/react/tooltip';
import { css, cva, cx } from '@repo/panda/css';
import type { CSSProperties, ReactNode, Ref } from 'react';
export type TooltipVariant = 'dark' | 'light';
export type TooltipSize = 'sm' | 'md' | 'lg' | 'fit';
export interface TooltipPopupProps {
  content: ReactNode;
  /** Unset (the default) follows the ambient ds-theme color mode; 'dark'/'light' force a fixed look regardless of theme. */
  variant?: TooltipVariant;
  size: TooltipSize;
  showArrow: boolean;
  contentClassName?: string;
  zIndex: number;
  contentRef?: Ref<HTMLDivElement>;
}

// Surface color flows through two custom properties, following the same convention as
// `@repo/ds-theme`'s `buttonRecipe` (`--ds-btn-bg`). Each variant sets `--ds-tooltip-surface`
// (the component's own default) and everything paints
// `var(--ds-tooltip-bg, var(--ds-tooltip-surface))`.
//
// The indirection is what makes overrides reliable. If the recipe declared `--ds-tooltip-bg`
// itself, a caller setting it through `contentClassName` would be a second single-class rule
// for the same property — which of the two wins comes down to stylesheet order, not intent.
// Because the recipe never declares `--ds-tooltip-bg`, a caller who sets it always wins, and
// the arrow picks the new color up along with the panel.
//
// The dark surfaces are lifted toward white by 8% (values inlined, not composed via a
// helper, so Panda's static extractor can still read them). That reproduces the legacy
// wm-tooltip's "theme background + 5% white overlay" — which computed to ~#26262d in the
// dark theme — as a single flat color the arrow can reuse. Without the lift, a raised
// surface sitting on a same-family dark canvas is nearly indistinguishable from it.
const tooltipContentStyles = cva({
  base: {
    borderRadius: '8',
    boxShadow: 'lg',
    // The one text style tooltips impose. `size` deliberately does not touch typography —
    // it sizes the box only — so content that needs different type brings its own.
    textStyle: 'bodyS',
    wordWrap: 'break-word',
    transitionProperty: '[opacity, transform]',
    transitionDuration: 'fast',
    transitionTimingFunction: 'easeOut'
  },
  variants: {
    variant: {
      // Default (no `variant` prop passed): follows the ambient ds-theme color mode.
      auto: {
        '--ds-tooltip-surface': 'color-mix(in srgb, token(colors.bg.raised) 92%, #fff)',
        backgroundColor: 'var(--ds-tooltip-bg, var(--ds-tooltip-surface))',
        color: 'text.highlight'
      },
      // Explicit overrides: force a specific look regardless of the ambient theme.
      dark: {
        '--ds-tooltip-surface': 'color-mix(in srgb, token(colors.brand.elevated) 92%, #fff)',
        backgroundColor: 'var(--ds-tooltip-bg, var(--ds-tooltip-surface))',
        color: 'text.onDark.strong'
      },
      light: {
        '--ds-tooltip-surface': 'token(colors.brand.white)',
        backgroundColor: 'var(--ds-tooltip-bg, var(--ds-tooltip-surface))',
        color: 'blackAlpha.90'
      }
    },
    // Box metrics only — padding and how wide the panel may grow before wrapping.
    size: {
      sm: {
        paddingX: '8',
        paddingY: '4',
        maxWidth: '[200px]',
        textAlign: 'center'
      },
      md: {
        paddingX: '12',
        paddingY: '8',
        maxWidth: '[320px]'
      },
      lg: {
        paddingX: '16',
        paddingY: '12',
        maxWidth: '[400px]'
      },
      fit: {
        paddingX: '12',
        paddingY: '8',
        maxWidth: '[max-content]'
      }
    }
  },
  defaultVariants: {
    variant: 'auto',
    size: 'md'
  }
});
export function TooltipPopup(t0) {
  const $ = _c(14);
  const {
    content,
    variant,
    size,
    showArrow,
    contentClassName,
    zIndex,
    contentRef
  } = t0;
  let t1;
  if ($[0] !== contentClassName || $[1] !== size || $[2] !== variant) {
    t1 = cx(tooltipContentStyles({
      variant: variant ?? "auto",
      size
    }), css({
      backdropFilter: "[blur(25px)]"
    }), contentClassName);
    $[0] = contentClassName;
    $[1] = size;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== zIndex) {
    t2 = {
      zIndex
    };
    $[4] = zIndex;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showArrow) {
    t3 = showArrow && <ArkTooltip.Arrow style={{
      "--arrow-size": "8px",
      "--arrow-background": "var(--ds-tooltip-bg, var(--ds-tooltip-surface))"
    } as CSSProperties}><ArkTooltip.ArrowTip /></ArkTooltip.Arrow>;
    $[6] = showArrow;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== content || $[9] !== contentRef || $[10] !== t1 || $[11] !== t2 || $[12] !== t3) {
    t4 = <Portal><ArkTooltip.Positioner><ArkTooltip.Content ref={contentRef} className={t1} style={t2}>{t3}{content}</ArkTooltip.Content></ArkTooltip.Positioner></Portal>;
    $[8] = content;
    $[9] = contentRef;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}